package com.neurobs.ccabserver.translation.states

import com.neurobs.ccab.translation.OrderedTranslationPhraseData
import com.neurobs.ccabserver.translation.util.{PhraseGroupManager, PhraseTranslationManager}
import com.neurobs.js.ccab.TranslationServer
import com.neurobs.js.util.ServerResponseUtil
import com.neurobs.react.{BooleanAttribute, GenericState, GenericStateGenerator, RegisteredChangeAttribute, StateAttribute}
import org.scalajs.dom.console
import slinky.core.FunctionalComponent
import slinky.core.annotations.react
import slinky.core.facade.Hooks._
import slinky.core.facade.ReactElement

case class PhraseSelectionState (
  phraseGroupSelectionState: PhraseGroupSelectionState,
  selectedPhraseState: StateAttribute[Option[PhraseTranslationManager]],
  loadingPhrasesState: BooleanAttribute,
  filterOnTranslationState: BooleanAttribute,
  registeredChangeState: RegisteredChangeAttribute
                                ) extends GenericState {

  def selectedPhraseGroupManager = phraseGroupSelectionState.selectedPhraseGroup

  def registerChange() = registeredChangeState.register()

  def setPhrases(phrases: Seq[OrderedTranslationPhraseData]) = {
    selectedPhraseGroupManager.map(_.setPhrases(phrases.toList))
    registerChange()
  }
  def clearPhrase() = voidState(selectedPhraseState)



  def selectedPhrase = selectedPhraseState.value
  def setSelectedPhrase(phraseTranslationManager: PhraseTranslationManager) = setOptionState(selectedPhraseState, phraseTranslationManager)
  def isSelected(phraseTranslationManager: PhraseTranslationManager) = selectedPhrase.map(_.phraseId == phraseTranslationManager.phraseId).getOrElse(false)

  def awaitingServerPhrases = loadingPhrasesState.value

  def filterOnTranslationStatus = filterOnTranslationState.value
  def toggleFilterStatus() = filterOnTranslationState.toggle()

  def loadPhrases(translatorId: Long) = {
    selectedPhraseGroupManager match {
      case None =>
      case Some(phraseGroupManager) =>
        phraseGroupManager.phrases match {
          case None =>
            loadingPhrasesState.setValue(true)
            TranslationServer.getPhrasesForGroup(phraseGroupManager.phraseGroupId, translatorId)(handleServerPhrases(phraseGroupManager))
          case Some(_) =>
            console.log("Phrases already loaded")
        }
    }
  }

  def handleServerPhrases(phraseGroupManager: PhraseGroupManager) = ServerResponseUtil.handleObjectResultResponse[Seq[OrderedTranslationPhraseData]](
    handleObject = data => {
      phraseGroupManager.setPhrases(data.toList)
      registerChange()
    },
    onAnyResponse = () => loadingPhrasesState.setValue(false)
  )

  def noteTranslation(phraseId: Long) = {
    phraseGroupSelectionState.phraseGroups match {
      case Left(_) =>
      case Right(phraseGroupManagers) =>
        phraseGroupManagers.foreach(_.markTranslatedPhrase(phraseId))
        registerChange()
    }
  }

  def nextPhrase(): Unit = {
    phraseGroupSelectionState.selectedPhraseGroup.map(phraseGroupManager => {
      selectedPhrase.map(selectedPhraseManager => {
        phraseGroupManager.phraseManagers.map(_.map(_._2).toList.sortBy(_.phraseIndex)).map(phraseManagerList => {
          phraseManagerList.indexOf(selectedPhraseManager) match {
            case index if index < 0 =>
            case index =>
              val nextIndex = if (index + 1 < phraseManagerList.length) index + 1 else 0
              setSelectedPhrase(phraseManagerList(nextIndex))
          }
        })
      })
    })
  }
}

object PhraseSelectionStateGenerator extends GenericStateGenerator[PhraseSelectionState] {
  override def generateEmptyDefaultState(): PhraseSelectionState = {
    PhraseSelectionState(
      PhraseGroupSelectionStateGenerator.generateEmptyDefaultState(),
      emptyOptionDefaultState[PhraseTranslationManager](),
      emptyBooleanState(),
      emptyBooleanState(),
      emptyRegisteredChangeState()
    )
  }
}

@react
object PhraseSelectionStateProvider {

  case class Props(children: ReactElement*)

  val component = FunctionalComponent[Props] { props =>

    val translatorState = TranslatorStateGenerator.accessState
    val phraseGroupSelectionState = PhraseGroupSelectionStateGenerator.accessState

    val selectedPhraseState = StateAttribute(useState[Option[PhraseTranslationManager]](None))
    val loadingPhrasesState = BooleanAttribute(useState(false))
    val filterOnTranslationState = BooleanAttribute(useState(false))
    val registeredChangeState = RegisteredChangeAttribute(useState(0))

    val phraseSelectionState = PhraseSelectionState(
      phraseGroupSelectionState = phraseGroupSelectionState,
      selectedPhraseState = selectedPhraseState,
      loadingPhrasesState = loadingPhrasesState,
      filterOnTranslationState = filterOnTranslationState,
      registeredChangeState = registeredChangeState
    )

    useEffect(() => {
      translatorState.selectedTranslator match {
        case None =>
        case Some(translator) =>
          phraseSelectionState.clearPhrase()
          phraseGroupSelectionState.selectedPhraseGroup match {
            case None =>
            case Some(_) =>
              phraseSelectionState.loadPhrases(translator.id)
          }
      }
    }, Vector(phraseGroupSelectionState.selectedPhraseGroup))

    useEffect(() => {
      phraseSelectionState.clearPhrase()
    }, Vector(translatorState.selectedTranslatorState.value))

    PhraseSelectionStateGenerator.Provider(phraseSelectionState)(
      props.children:_*
    )
  }
}
